import CheckmarkIcon from '@/assets/images/icons/checkmark_icon.svg'
import React, { useEffect, useState } from 'react'
import { CoursePagePremiumLoader } from '@/components/course/CoursePagePremiumLoader'
import { Alert, Image, StyleSheet } from 'react-native'
import { Button, ScrollView, Text, useMedia, View, XStack, YStack } from 'tamagui'
import { BodyText, ContentTitle, EyebrowText } from '@/components/ui/Typography'

import { LessonActions } from '@/components/lesson/LessonActions'
import { logger } from '@/lib/logger'
import { isLessonBookmarked } from '@/supabase/services'
import { Lesson } from '@/types'
import { buildLessonUrl } from '@/utils/buildLessonUrl'
import { useResponsiveImageUrl } from '@/utils/imageUtils'
import { getLessonStructuredTitleMeta, LESSON_META_COLOR } from '@/utils/lessonStructuredTitle'

type LessonTitleBlockProps = {
  displayTitle: string
  metaLine: string | null
  isCompleted: boolean
  isWeb: boolean
}

function LessonTitleBlock({ displayTitle, metaLine, isCompleted, isWeb }: LessonTitleBlockProps) {
  if (isWeb) {
    return (
      <YStack pb="$2" gap="$1">
        {metaLine ? (
          <EyebrowText color={LESSON_META_COLOR} textTransform="capitalize">
            {metaLine}
          </EyebrowText>
        ) : null}
        <XStack alignItems="flex-start" gap="$2">
          <ContentTitle color="$color" flex={1}>
            {displayTitle}
          </ContentTitle>
          {isCompleted && (
            <XStack p="$2" pr={0}>
              <CheckmarkIcon width={20} height={20} />
            </XStack>
          )}
        </XStack>
      </YStack>
    )
  }

  return (
    <YStack gap="$2">
      {metaLine ? (
        <EyebrowText color={LESSON_META_COLOR} textTransform="capitalize">
          {metaLine}
        </EyebrowText>
      ) : null}
      <XStack alignItems="flex-start" gap="$2">
        <ContentTitle color="$color" letterSpacing={0.25} flex={1}>
          {displayTitle}
        </ContentTitle>
        {isCompleted && (
          <XStack p="$1">
            <CheckmarkIcon width={18} height={18} />
          </XStack>
        )}
      </XStack>
    </YStack>
  )
}

interface LessonContentProps {
  lesson: Lesson | null
  lessonLoading?: boolean
  lessonError?: string | null
  onPlayPress: () => void
  onRetry?: () => void
  isWeb?: boolean
  onBookmarkSave?: (isBookmarked: boolean) => void
  onBookmarkIconPress?: () => void
  showError: (msg: string) => void
  showSuccess: (msg: string) => void
  userId?: string
  collectionTitle?: string
  collectionId?: number
  referrer?: string
  referrerTitle?: string
  courseTitle?: string
  courseSlug: string
  isCompleted?: boolean
  hidePublishedDate?: boolean
  isAnon?: boolean
}

export function LessonContent({
  lesson,
  lessonLoading = false,
  lessonError = null,
  onPlayPress,
  onRetry = () => {},
  isWeb = false,
  onBookmarkSave,
  onBookmarkIconPress,
  showError,
  showSuccess,
  userId,
  collectionId,
  courseSlug,
  referrer,
  referrerTitle,
  courseTitle,
  isCompleted = false,
  hidePublishedDate = false,
  isAnon = false,
}: LessonContentProps) {
  const { short } = useMedia();
  const [isBookmarked, setIsBookmarked] = useState(false);

  const responsiveImageUrl = useResponsiveImageUrl(lesson?.thumbnail || '', 'card')
  useEffect(() => {
    if (!isAnon && userId && lesson?.id) {
        isLessonBookmarked(userId, lesson.id)
          .then(setIsBookmarked)
          .catch((error) => {
            logger.error('Failed to check if lesson is bookmarked:', { lessonId: lesson.id, userId, error })
          });
    }
  }, [lesson, userId, isAnon]);

  const handleBookmarkSave = (isNowBookmarked: boolean) => {
    setIsBookmarked(isNowBookmarked)
    onBookmarkSave?.(isNowBookmarked)
  }

  if (lessonLoading) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding="$4">
        <CoursePagePremiumLoader variant="lesson" />
      </YStack>
    )
  }

  if (lessonError) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding="$4">
        <Text color="#ff0000" textAlign="center">{lessonError}</Text>
        <Button 
          marginTop="$3" 
          onPress={onRetry}
        >
          Retry
        </Button>
      </YStack>
    )
  }

  if (!lesson) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding="$4">
        <Text color="#666">No lesson data available</Text>
      </YStack>
    )
  }

  const { displayTitle, metaLine } = getLessonStructuredTitleMeta(lesson)

  if (isWeb) {
    return (
      <YStack flex={1} height="100%">
        <View 
          width="100%" 
          minWidth="100%"
          aspectRatio={16/9} 
          maxHeight="50%" 
          overflow="hidden" 
          backgroundColor="#eee"
        >
          {responsiveImageUrl ? (
            <Image
              source={{ uri: responsiveImageUrl }}
              style={styles.webVideoThumbnail}
              resizeMode="cover"
            />
          ) : (
            <View 
              width="100%" 
              height="100%" 
              backgroundColor="#f0f0f0" 
              justifyContent="center" 
              alignItems="center"
            >
              <BodyText color="#666">No Thumbnail Available</BodyText>
            </View>
          )}
        </View>

        <XStack
          paddingHorizontal="$6"
          paddingVertical="$5"
          justifyContent="space-between"
          alignItems="center"
        >
          <LessonActions
            lesson={lesson}
            onPlayPress={onPlayPress}
            onSharePress={() => Alert.alert('Share pressed')}
            lessonUrl={buildLessonUrl({
              lessonSlug: lesson.slug,
              courseSlug,
              collectionId,
              referrer,
              referrerTitle,
              courseTitle,
            })}
            onBookmarkSave={handleBookmarkSave}
            onBookmarkIconPress={onBookmarkIconPress}
            showError={showError}
            showSuccess={showSuccess}
            isBookmarked={isBookmarked}
            publishedAt={lesson.published_at}
            isAnon={isAnon}
          />
        </XStack>

        <ScrollView
          flex={1}
          showsVerticalScrollIndicator={false}
          paddingBottom="$5"
        >
          {lesson.summary.trim() && (
            <YStack paddingHorizontal="$6" paddingBottom="$3">
              <LessonTitleBlock
                displayTitle={displayTitle}
                metaLine={metaLine}
                isCompleted={isCompleted}
                isWeb
              />
              <BodyText color="$color">
                {lesson.summary}
              </BodyText>
              {lesson.published_at && !hidePublishedDate && (
                <EyebrowText
                  color={LESSON_META_COLOR}
                  marginTop="$2"
                >
                  Published:{' '}
                  {new Date(lesson.published_at as any).toLocaleDateString('en-US', {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </EyebrowText>
              )}
            </YStack>
          )}
        </ScrollView>
      </YStack>
    )
  }

  return (
    <YStack flex={1} width="100%">
      {!short && (
        <View width="100%" aspectRatio={16 / 9} overflow="hidden" backgroundColor="#eee">
          {responsiveImageUrl ? (
            <Image source={{ uri: responsiveImageUrl }} style={styles.videoThumbnail} resizeMode="cover" />
          ) : (
            <View width="100%" height="100%" backgroundColor="#f0f0f0" justifyContent="center" alignItems="center">
              <BodyText color="#666">No Thumbnail Available</BodyText>
            </View>
          )}
        </View>
      )}

      <XStack
        paddingHorizontal="$4"
        paddingVertical="$5"
        justifyContent="space-between"
        alignItems="center"
      >
        <LessonActions
          lesson={lesson}
          onPlayPress={onPlayPress}
          onSharePress={() => Alert.alert('Share pressed')}
          lessonUrl={buildLessonUrl({
            lessonSlug: lesson.slug,
            courseSlug,
            collectionId,
            referrer,
            referrerTitle,
            courseTitle,
          })}
          onBookmarkSave={handleBookmarkSave}
          onBookmarkIconPress={onBookmarkIconPress}
          showError={showError}
          showSuccess={showSuccess}
          isBookmarked={isBookmarked}
          publishedAt={lesson.published_at}
        />
      </XStack>

      {lesson.summary.trim() && (
        <YStack gap="$4" mb="$5" paddingHorizontal="$4">
          <LessonTitleBlock
            displayTitle={displayTitle}
            metaLine={metaLine}
            isCompleted={isCompleted}
            isWeb={false}
          />
          <BodyText color="$color">
            {lesson.summary}
          </BodyText>
          {lesson.published_at && !hidePublishedDate && (
            <EyebrowText
              color={LESSON_META_COLOR}
              marginTop="$2"
            >
              Published:{' '}
              {new Date(lesson.published_at as any).toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
              })}
            </EyebrowText>
          )}
        </YStack>
      )}
    </YStack>
  )
}

const styles = StyleSheet.create({
  webVideoThumbnail: { width: '100%', height: '100%' },
  videoThumbnail: { width: '100%', height: '100%' },
})